import { defaultDataIdFromObject, InMemoryCache } from "apollo-cache-inmemory";
import { ApolloClient } from "apollo-client";
import { DefaultOptions } from "apollo-client/ApolloClient";
import { ApolloLink, NextLink, split } from "apollo-link";
import { BatchHttpLink } from "apollo-link-batch-http";
import { setContext } from "apollo-link-context";
import { onError } from "apollo-link-error";
import { HttpLink } from "apollo-link-http";
import { WebSocketLink } from "apollo-link-ws";
import { getMainDefinition } from "apollo-utilities";
import Vue from "vue";
import VueApollo from "vue-apollo";

import { trackException } from "./ga";
import {
  BATCHING_INTERVAL,
  ENABLE_BATCHING,
  getBackendHostAndPort,
  PRODUCTION_MODE,
} from "./globals";
import { hideMainSpinner } from "./lib/utils";
import store from "./store";
import { ErrorType } from "./store/modules/error";
import { getJwt } from "./token";

const { BACKEND_HTTP_URI, BACKEND_WS_URI } = getBackendHostAndPort();
// console.log({ BACKEND_HTTP_URI, BACKEND_WS_URI });
//
const GET_WHITE_LIST = {
  BookMastersByFilterAndOrder: {},
  HighestBookAskingPrice: {},
  BookCategories: {},
  BookDetails: {},
  BookSeries: {},
  RelatedBookMasters: {},
  GameList: {},
  GamesItemListings: {
    guestOnly: true,
  },
};

const isOperationCacheable = (operationName) => {
  const userToken = getJwt();
  const whiteItem = GET_WHITE_LIST[operationName];
  if (!whiteItem) {
    return false;
  }
  if (userToken && whiteItem.guestOnly === true) {
    return false;
  }
  return true;
};

const cache = new InMemoryCache({
  dataIdFromObject: (object) => {
    const obj: any = object;
    switch (obj.__typename) {
      case "User":
        return obj.email;
      case "Game":
        return obj.slug;
      case "ImageDescription":
        return obj.url;
      default:
        return defaultDataIdFromObject(object);
    }
  },
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach((errors) => {
      if (!errors) {
        trackException(new Error("Undefined graphql error"));
        return;
      }

      const { message, locations, path } = errors;

      const msg = `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`;
      store.commit("error/push", {
        type: ErrorType.GRAPHQL_ERROR,
        message: msg,
      });
      trackException(new Error(msg));
    });
  }

  if (networkError) {
    console.error(`[Network error]: ${networkError}`);
    store.commit("error/push", {
      type: ErrorType.NETWORK_ERROR,
      message: networkError,
    });
    trackException(networkError);
  }
});

const authLink = setContext(({ operationName }, { headers }) => {
  if (operationName && isOperationCacheable(operationName)) {
    return;
  }
  const token = getJwt();
  return {
    headers: {
      ...headers,
      Authorization: token ? `JWT ${token}` : "",
    },
  };
});

const langLink = setContext((_, { headers }) => {
  const lang = localStorage.getItem("lang");
  return {
    headers: {
      ...headers,
      "Accept-Language": lang ? lang : "en",
    },
  };
});

let remoteLink: ApolloLink;

const httpLink = ENABLE_BATCHING
  ? new BatchHttpLink({
      uri: `${BACKEND_HTTP_URI}batch/`,
      batchInterval: BATCHING_INTERVAL,
    })
  : new HttpLink({
      uri: BACKEND_HTTP_URI,
    });

const getHttpLink = new HttpLink({
  uri: BACKEND_HTTP_URI,
  useGETForQueries: true,
  // We need to set this to something that
  // a) Will be CORS-safe
  // b) doesn't make Graphene-Django think that we have a POST-body
  headers: { "content-type": "text/plain" },
});

// Confusing fact of the day:
// If the split condition returns true, the FIRST link is used
// End of confusing fact of the day
//
// Justus
const httpSplit = split(
  ({ query }) => {
    const { name } = getMainDefinition(query);
    if (!name) {
      console.warn("Query without name", query);
      return false;
    }
    return isOperationCacheable(name.value);
  },
  getHttpLink,
  httpLink,
);

const WEB_SOCKET_EMABLED = false;

if (WEB_SOCKET_EMABLED) {
  const wsLink = new WebSocketLink({
    uri: BACKEND_WS_URI,
    options: {
      reconnect: false, //Disabled until backend subscription implementation
    },
  });

  remoteLink = split(
    // split based on operation type
    ({ query }) => {
      const { kind, operation } = getMainDefinition(query);
      return kind === "OperationDefinition" && operation === "subscription";
    },
    wsLink,
    httpSplit,
  );
} else {
  remoteLink = httpSplit;
}

const defaultOptions: DefaultOptions = {
  watchQuery: {
    fetchPolicy: "no-cache",
    errorPolicy: "ignore",
  },
  query: {
    fetchPolicy: "no-cache",
    errorPolicy: "all",
  },
};

export let firstOperationRequested = false;
const spinnerLink = new ApolloLink((operation, forward: NextLink) => {
  const observable = forward(operation);
  if (firstOperationRequested) {
    return observable;
  }
  firstOperationRequested = true;
  observable.subscribe((val) => {
    hideMainSpinner();
  });
  return observable;
});

// Create the apollo client
export const apolloClient = new ApolloClient({
  link: ApolloLink.from([
    spinnerLink,
    errorLink,
    authLink,
    langLink,
    remoteLink,
  ]),
  cache,
  connectToDevTools: !PRODUCTION_MODE,
  defaultOptions,
});

export const apolloProvider = new VueApollo({
  defaultClient: apolloClient,
});

Vue.use(VueApollo);
